import { useCallback, useEffect, useState } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  describeExpiry,
  fetchMembers,
  fetchPending,
  invite,
  leaveAccount,
  removeMember,
  whoAmI,
  withdraw,
  type InviteResult,
  type Member,
  type Pending,
  type WhoAmI,
} from '../account/sharing';
import { translate, useT } from '../i18n/i18n';

export default function PeopleSection() {
  const t = useT();
  const [me, setMe] = useState<WhoAmI | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [pending, setPending] = useState<Pending[]>([]);
  const [email, setEmail] = useState('');
  const [issued, setIssued] = useState<InviteResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [removing, setRemoving] = useState<Member | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const who = await whoAmI();
      setMe(who);
      setMembers(await fetchMembers());
      setPending(who.ownAccount ? await fetchPending() : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : translate('We could not load who is on this account.'));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function act<T>(fn: () => Promise<T>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That did not work.'));
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const result = await invite(email.trim());
      setIssued(result);
      setEmail('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That invitation could not be sent.'));
    } finally {
      setBusy(false);
    }
  }

  if (!me) {
    return (
      <div className="card">
        <h2>{t('People')}</h2>
        {error ? <div className="error" role="alert">{error}</div> : <p className="muted">{t('Loading…')}</p>}
      </div>
    );
  }

  /* A MEMBER SEES WHO ELSE IS HERE AND THE WAY OUT, AND NOTHING ELSE. Inviting and removing
     belong to whoever owns the learners' records. */
  if (!me.ownAccount) {
    return (
      <div className="card">
        <h2>{t('People')}</h2>
        <p className="field-hint" style={{ marginTop: 0 }}>
          {t("You are helping with someone else's learners. You can run programs and see how they are getting on; the subscription and the account itself stay with their owner.")}
        </p>
        {error && <div className="error" role="alert">{error}</div>}
        <ul className="list">
          {members.map((m) => (
            <li className="list-item" key={m.memberId}>
              <div className="list-item-text">
                <div className="name">
                  {m.displayName || m.email || t('Someone')}
                  {m.you && <span className="pill">{t('You')}</span>}
                </div>
                <div className="list-item-meta">
                  {m.role === 'OWNER' ? t('Owns this account') : t('Helping out')}
                </div>
              </div>
            </li>
          ))}
        </ul>
        <div className="form-actions">
          <button className="btn quiet-danger" onClick={() => setLeaving(true)} disabled={busy}>
            {t('Leave this account')}
          </button>
        </div>
        <ConfirmDialog
          open={leaving}
          title={t('Leave this account?')}
          message={t("You will stop seeing this person's learners. Nothing of theirs is deleted, and nothing of yours is either — they can invite you back at any time.")}
          confirmLabel={t('Leave')}
          cancelLabel={t('Stay')}
          destructive
          onConfirm={() => { setLeaving(false); void act(leaveAccount); }}
          onCancel={() => setLeaving(false)}
        />
      </div>
    );
  }

  return (
    <div className="card">
      <h2>{t('People')}</h2>
      <p className="field-hint" style={{ marginTop: 0 }}>
        {t('Another adult — an assistant, a teacher, the other parent — can run programs and see how a learner is getting on. They cannot change the subscription or delete the account.')}
      </p>

      {error && <div className="error" role="alert">{error}</div>}

      <ul className="list">
        {members.map((m) => (
          <li className="list-item" key={m.memberId}>
            <div className="list-item-text">
              <div className="name">
                {m.displayName || m.email || t('Someone')}
                {m.you && <span className="pill">{t('You')}</span>}
              </div>
              <div className="list-item-meta">
                {m.role === 'OWNER' ? t('Owns this account') : m.email ?? t('Helping out')}
              </div>
            </div>
            {m.role === 'MEMBER' && (
              <div className="toolbar">
                <button
                  className="btn quiet-danger"
                  disabled={busy}
                  onClick={() => setRemoving(m)}
                  aria-label={t('Remove {name}', { name: m.displayName || m.email || t('this person') })}
                >
                  {t('Remove')}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      <h3>{t('Invite somebody')}</h3>
      <div className="row">
        <div className="field">
          <label htmlFor="invite-email">{t('Their email address')}</label>
          <input
            id="invite-email"
            type="email"
            autoComplete="off"
            value={email}
            maxLength={254}
            onChange={(e) => { setEmail(e.target.value); setIssued(null); }}
          />
          <span className="field-hint">
            {t('They will need an account of their own. If they have not got one, they can make one when they follow the link.')}
          </span>
        </div>
      </div>
      <div className="form-actions">
        <button className="btn" onClick={() => void send()} disabled={busy || !email.trim()}>
          {busy ? t('Working…') : t('Send an invitation')}
        </button>
      </div>

      {issued && (
        <div className="banner" role="status">
          {issued.emailed ? (
            <>{t('An invitation has gone to')} <strong>{issued.email}</strong>, {describeExpiry(issued.expiresAtEpochMs)}.</>
          ) : (
            <>
              <p style={{ marginTop: 0 }}>
                {t('We could not send the email just now, so here is the link — send it to')}{' '}
                <strong>{issued.email}</strong> {t('yourself.')} {t('It is {expiry}, and anybody holding it can accept, so pass it on privately.', { expiry: describeExpiry(issued.expiresAtEpochMs) })}
              </p>
              <input
                readOnly
                value={issued.link}
                onFocus={(e) => e.currentTarget.select()}
                aria-label={t('Invitation link')}
              />
            </>
          )}
        </div>
      )}

      {pending.length > 0 && (
        <>
          <h3>{t('Waiting to be accepted')}</h3>
          <ul className="list">
            {pending.map((p) => (
              <li className="list-item" key={p.invitationId}>
                <div className="list-item-text">
                  <div className="name">{p.email}</div>
                  <div className="list-item-meta">{describeExpiry(p.expiresAtEpochMs)}</div>
                </div>
                <div className="toolbar">
                  <button
                    className="btn secondary"
                    disabled={busy}
                    onClick={() => void act(() => withdraw(p.invitationId))}
                  >
                    {t('Withdraw')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <ConfirmDialog
        open={removing !== null}
        title={t('Remove this person?')}
        message={
          removing
            ? (removing.displayName || removing.email
              ? t('{name} will stop seeing your learners straight away. Nothing they recorded is deleted.', { name: removing.displayName || removing.email || '' })
              : t('They will stop seeing your learners straight away. Nothing they recorded is deleted.'))
            : ''
        }
        confirmLabel={t('Remove')}
        cancelLabel={t('Keep')}
        destructive
        onConfirm={() => {
          const target = removing;
          setRemoving(null);
          if (target) void act(() => removeMember(target.memberId));
        }}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
